'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const revenueMonths = [
    { month: 'Oct', value: 68 },
    { month: 'Nov', value: 74 },
    { month: 'Dec', value: 58 },
    { month: 'Jan', value: 82 },
    { month: 'Feb', value: 91 },
    { month: 'Mar', value: 100 },
];

const transactions = [
    { student: 'Ama Owusu', type: 'Monthly Tuition', amount: 'NGN 450', date: 'Mar 1', status: 'paid' },
    { student: 'Kwame Asante', type: 'Session Fee', amount: 'NGN 120', date: 'Mar 3', status: 'paid' },
    { student: 'Efua Mensah', type: 'Monthly Tuition', amount: 'NGN 450', date: 'Mar 1', status: 'paid' },
    { student: 'Kofi Boateng', type: 'Monthly Tuition', amount: 'NGN 450', date: 'Mar 5', status: 'pending' },
];

export default function StudioAnalytics() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="analytics" ref={sectionRef} className="py-28 px-6 md:px-10 relative overflow-hidden" style={{ background: '#F5F6FF' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 100%, rgba(55,87,255,0.06) 0%, transparent 60%)' }} />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <div
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                        style={{ background: 'rgba(55,87,255,0.1)', color: BRAND, border: '1px solid rgba(55,87,255,0.2)', letterSpacing: '0.15em' }}
                    >
                        Analytics & Billing
                    </div>
                    <div className="grid lg:grid-cols-2 gap-8 items-end">
                        <h2
                            className="font-display font-800"
                            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                        >
                            Data that drives
                            <br />
                            <span style={{ color: BRAND }}>better decisions.</span>
                        </h2>
                        <p className="text-base leading-relaxed" style={{ color: '#666', maxWidth: '420px' }}>
                            Track student performance, engagement, and institutional KPIs alongside transparent billing and financial reporting — all in one view.
                        </p>
                    </div>
                </div>

                {/* Bento grid */}
                <div className={`grid lg:grid-cols-3 gap-5 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {/* KPI cards row */}
                    {[
                        { label: 'Total Revenue', value: 'NGN 48,200', sub: 'This month', highlight: true },
                        { label: 'Student Retention', value: '94%', sub: '+3% vs last month', highlight: false },
                        { label: 'Avg Engagement', value: '87%', sub: 'Session attendance rate', highlight: false },
                    ].map((kpi, i) => (
                        <div
                            key={kpi.label}
                            className="rounded-2xl p-6 transition-all duration-300"
                            style={{
                                background: kpi.highlight ? BRAND : 'white',
                                border: kpi.highlight ? 'none' : '1px solid rgba(55,87,255,0.1)',
                                transitionDelay: `${i * 60}ms`,
                            }}
                            onMouseEnter={e => { if (!kpi.highlight) { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 32px rgba(55,87,255,0.1)'; } }}
                            onMouseLeave={e => { if (!kpi.highlight) { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; } }}
                        >
                            <p className="text-xs font-700 uppercase tracking-widest mb-3" style={{ color: kpi.highlight ? 'rgba(255,255,255,0.6)' : '#999', letterSpacing: '0.12em' }}>{kpi.label}</p>
                            <p className="text-3xl font-800 mb-1" style={{ color: kpi.highlight ? 'white' : BRAND }}>{kpi.value}</p>
                            <p className="text-xs font-600" style={{ color: kpi.highlight ? 'rgba(255,255,255,0.6)' : '#888' }}>{kpi.sub}</p>
                        </div>
                    ))}

                    {/* Revenue chart — spans 2 cols */}
                    <div className="lg:col-span-2 rounded-3xl p-7" style={{ background: 'white', border: '1px solid rgba(55,87,255,0.1)' }}>
                        <div className="flex items-center justify-between mb-6">
                            <p className="text-sm font-800" style={{ color: '#0D0D0D' }}>Revenue Trend</p>
                            <span className="text-xs font-600 px-3 py-1.5 rounded-full" style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}>Last 6 months</span>
                        </div>
                        <div className="flex items-end gap-3 mb-4" style={{ height: '100px' }}>
                            {revenueMonths.map((m) => (
                                <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
                                    <div
                                        className="w-full rounded-lg transition-all duration-700"
                                        style={{
                                            height: visible ? `${m.value}px` : '0px',
                                            background: m.month === 'Mar' ? BRAND : 'rgba(55,87,255,0.15)',
                                            transitionDelay: `${revenueMonths.indexOf(m) * 80}ms`,
                                        }}
                                    />
                                    <span className="text-xs font-700" style={{ color: m.month === 'Mar' ? BRAND : '#888' }}>{m.month}</span>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center gap-2 pt-4" style={{ borderTop: '1px solid rgba(55,87,255,0.08)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: '#10B981' }} />
                            <span className="text-xs font-600" style={{ color: '#10B981' }}>+18% growth vs same period last year</span>
                        </div>
                    </div>

                    {/* Performance breakdown */}
                    <div className="rounded-3xl p-7" style={{ background: 'white', border: '1px solid rgba(55,87,255,0.1)' }}>
                        <p className="text-sm font-800 mb-5" style={{ color: '#0D0D0D' }}>Performance Breakdown</p>
                        <div className="flex flex-col gap-4">
                            {[
                                { label: 'A (90–100%)', count: 42, pct: 28 },
                                { label: 'B (75–89%)', count: 68, pct: 46 },
                                { label: 'C (60–74%)', count: 28, pct: 19 },
                                { label: 'Below 60%', count: 10, pct: 7 },
                            ].map((band, i) => (
                                <div key={band.label}>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <p className="text-xs font-700" style={{ color: '#0D0D0D' }}>{band.label}</p>
                                        <span className="text-xs font-600" style={{ color: '#888' }}>{band.count} students</span>
                                    </div>
                                    <div className="w-full h-2 rounded-full" style={{ background: 'rgba(55,87,255,0.08)' }}>
                                        <div
                                            className="h-2 rounded-full transition-all duration-1000"
                                            style={{
                                                width: visible ? `${band.pct}%` : '0%',
                                                background: i === 0 ? '#10B981' : i === 1 ? BRAND : i === 2 ? '#F59E0B' : '#EF4444',
                                                transitionDelay: `${i * 100}ms`,
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Billing / transactions — spans 3 cols */}
                    <div className="lg:col-span-3 rounded-3xl p-7" style={{ background: 'white', border: '1px solid rgba(55,87,255,0.1)' }}>
                        <div className="flex items-center justify-between mb-6">
                            <p className="text-sm font-800" style={{ color: '#0D0D0D' }}>Recent Transactions</p>
                            <a
                                href="#"
                                className="text-xs font-700 transition-colors"
                                style={{ color: BRAND }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                            >
                                View all billing →
                            </a>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {transactions.map((tx, i) => (
                                <div
                                    key={`${tx.student}-${i}`}
                                    className="rounded-2xl p-5 transition-all duration-200"
                                    style={{ background: '#FAFAFA', border: '1px solid rgba(55,87,255,0.06)' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.2)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.06)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs font-600" style={{ color: '#888' }}>{tx.date}</span>
                                        <span
                                            className="text-xs font-700 px-2 py-0.5 rounded-full"
                                            style={{
                                                background: tx.status === 'paid' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                                                color: tx.status === 'paid' ? '#10B981' : '#F59E0B',
                                            }}
                                        >
                                            {tx.status === 'paid' ? 'Paid' : 'Pending'}
                                        </span>
                                    </div>
                                    <p className="text-sm font-800 mb-0.5" style={{ color: '#0D0D0D' }}>{tx.amount}</p>
                                    <p className="text-xs font-600 mb-0.5" style={{ color: '#555' }}>{tx.student}</p>
                                    <p className="text-xs font-500" style={{ color: '#AAA' }}>{tx.type}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
